import React, { useCallback, useEffect } from 'react';
import Page from '../../components/Page/Page';
import Title from '../../components/Page/Title';
import { useTranslation } from 'react-i18next';
import Paper from '../../components/Paper/Paper';
import { List, Row, Typography, Popconfirm } from 'antd';
import { DeleteOutlined, EnvironmentOutlined, PlusOutlined } from '@ant-design/icons';
import { Location } from '../../api/models/locations';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/selectors/user';
import { CardTagDefault } from '../../components/CardTag/CardTag';
import Button from '../../components/Button/Button';
import { AppDispatch } from '../../types/store';
import { whenAddressIsFailedToBeRemoved, whenAddressIsRemoved } from './messages';
import { getErrorFromResponse } from '../../utils/apiHelpers';
import EmptyPage, { EMPTY_PAGE_USECASE } from '../pageNotFound/EmptyPage';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import {
  addressesScreenOpen,
  createNewAddressClicked,
  removeAddressClicked,
  setDefaultAddressClicked
} from '../../utils/analytika/events';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';

export const postAddressDeletion = (error?: any) => {
  if (!error) {
    whenAddressIsRemoved();
  } else {
    whenAddressIsFailedToBeRemoved(getErrorFromResponse(error));
  }
};

const AddressesV2 = () => {
  const { t } = useTranslation();
  const { pickupLocations, defaultPickupLocation } = useSelector(userSelector);
  const {
    user: { storeDefaultPickupLocation: setDefaultPickupLocation, removeAddress },
    modals: { toggleIsAddNewPickupVisible }
  } = useDispatch<AppDispatch>();
  const { xs: isXsBreakpoint, md: isMdBreakpoint } = useBreakpoint();

  const {
    user: { publishAnalytikaEvent }
  } = useDispatch<AppDispatch>();

  useEffect(() => {
    publishAnalytikaEvent(
      addressesScreenOpen({ saved_addresses: pickupLocations, saved_address_count: pickupLocations.length })
    );
  }, []);

  const onRemoveAddress = useCallback(async (id: string | number) => {
    try {
      await removeAddress(id);
      postAddressDeletion();
    } catch (err) {
      postAddressDeletion(err);
    }
  }, []);

  const onSetDefaultPickup = useCallback((location: Location) => {
    try {
      setDefaultPickupLocation(location);
    } catch (err) {
      // instrumentation
    }
  }, []);

  const defaultPickupId = defaultPickupLocation ? defaultPickupLocation.id : null;

  return (
    <Page>
      {isMdBreakpoint && <Title text={t('addresses.addressesTitle')} />}

      {pickupLocations.length ? (
        <Paper>
          <Typography.Title type="secondary" level={4}>
            {t(`addresses.savedAddressesTitle`, { count: pickupLocations.length })}
          </Typography.Title>
          <List
            dataSource={pickupLocations}
            itemLayout={isXsBreakpoint ? 'vertical' : 'horizontal'}
            renderItem={(locationItem: Location) => {
              const { name, nickname, id } = locationItem;
              const isDefault = Boolean(defaultPickupId && defaultPickupId === id);

              const actions = [
                <Popconfirm
                  key={`${id}-remove`}
                  title={t('addresses.delete.popTitle')}
                  description={t('addresses.delete.popText')}
                  onConfirm={async () => {
                    publishAnalytikaEvent(removeAddressClicked({ address_removed: locationItem }));
                    publishAV2Event({
                      event_name: ANALYTIKA_EVENTS_V2.EXPR_REMOVE_ADDRESS,
                      event_version: 2,
                      picked_full_address: locationItem.nickname
                    });
                    await onRemoveAddress(id);
                  }}
                  okText={t('addresses.delete.popConfirm')}
                  cancelText={t('addresses.delete.popCancel')}
                  okButtonProps={{ danger: true, size: 'small', style: { padding: '0 7px', height: 24 } }}
                  cancelButtonProps={{ size: 'small', style: { padding: '0 7px', height: 24 } }}
                >
                  <Button
                    danger
                    style={{ padding: isXsBreakpoint ? 0 : 'inherit' }}
                    icon={<DeleteOutlined />}
                    type="link"
                    key={`${id}-remove`}
                  >
                    {t('general.remove')}
                  </Button>
                </Popconfirm>
              ];

              !isDefault &&
                actions.unshift(
                  <Button
                    hidden={isDefault}
                    type="link"
                    style={{ padding: isXsBreakpoint ? 0 : 'inherit' }}
                    key={`${id}-setdefault`}
                    onClick={() => {
                      publishAnalytikaEvent(setDefaultAddressClicked({ address_marked_as_default: locationItem }));
                      publishAV2Event({
                        event_name: ANALYTIKA_EVENTS_V2.EXPR_DEFAULT_ADDRESS,
                        event_version: 2,
                        picked_full_address: locationItem.nickname
                      });
                      onSetDefaultPickup(locationItem);
                    }}
                  >
                    {t('general.setAsDefaultBtn')}
                  </Button>
                );

              return (
                <List.Item actions={actions}>
                  <List.Item.Meta
                    avatar={
                      <div style={{ lineHeight: 4 }}>
                        <EnvironmentOutlined style={{ fontSize: 24 }} />
                      </div>
                    }
                    title={<Typography.Title level={4}>{nickname}</Typography.Title>}
                    description={
                      <>
                        <Row>
                          <Typography.Text type="secondary">{name}</Typography.Text>
                        </Row>
                        {isDefault && (
                          <Row style={{ marginTop: 5 }}>
                            <CardTagDefault />
                          </Row>
                        )}
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
          <Button
            block={isXsBreakpoint}
            style={{ marginTop: 10 }}
            icon={<PlusOutlined />}
            type="default"
            onClick={() => {
              publishAnalytikaEvent(createNewAddressClicked());
              toggleIsAddNewPickupVisible();
            }}
          >
            {t('profile.addNewAddress')}
          </Button>
        </Paper>
      ) : (
        <EmptyPage useCase={EMPTY_PAGE_USECASE.ADDRESSES_EMPTY} hasCTA={true} ctaAction={toggleIsAddNewPickupVisible} />
      )}
    </Page>
  );
};

export default AddressesV2;
